import { Country } from '@/constructs/Country';
import { DTO, Nullable } from '@/type-utils';

import { Brand } from '@/constructs/Brand';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import type { IShippingMethod } from '.';
import Model from '../Model';
import { MoneyModel } from '../Money';
/** Represents a shipping method. */
export default class ShippingMethodModel
  extends Model<DTO<IShippingMethod>>
  implements IShippingMethod
{
  /** @inheritdoc */
  public readonly id: string;

  /** @inheritdoc */
  public readonly uid: string;

  /** @inheritdoc */
  public readonly country: Country;

  /** @inheritdoc */
  public readonly brand: Brand;

  /** @inheritdoc */
  public readonly name: string;

  /** @inheritdoc */
  public readonly description: string;

  /** @inheritdoc */
  public readonly shippingCost: MoneyModel;

  /** @inheritdoc */
  public readonly latestDeliveryDays: Nullable<number>;

  /** @inheritdoc */
  public readonly earliestDeliveryDays: Nullable<number>;

  /** @inheritdoc */
  public readonly tax: MoneyModel;

  /** @inheritdoc */
  public readonly earliestDeliveryDateOverride: Nullable<string>;

  /** @inheritdoc */
  public readonly latestDeliveryDateOverride: Nullable<string>;

  /** @inheritdoc */
  public readonly formattedDeliveryEstimate: Nullable<string>;

  /** @inheritdoc */
  public readonly deliveryMessageHTML: Nullable<string>;

  /** @inheritdoc */
  public readonly additionalMessage: Nullable<string>;

  /** @inheritdoc */
  public readonly cutoffTime: string;

  /** @inheritdoc */
  public readonly cutoffDisplay: number;

  private _taxRate: number;

  /** @inheritdoc */
  public constructor(shippingMethod: DTO<IShippingMethod>) {
    super(shippingMethod);

    this.id = shippingMethod.id;
    this.uid = shippingMethod.uid;
    this.country = shippingMethod.country;
    this.brand = shippingMethod.brand;
    this.name = shippingMethod.name;
    this.description = shippingMethod.description;
    this.cutoffTime = shippingMethod.cutoffTime;

    this.shippingCost = MoneyModel.from(shippingMethod.shippingCost);
    if ('cutoffDisplay' in shippingMethod) {
      this.cutoffDisplay = shippingMethod.cutoffDisplay;
    } else {
      this.cutoffDisplay = ConfigurationService.getConfig(
        'shippingMethods'
      ).getSetting('overrides.cutoffDisplayHours').value;
    }
    this.tax = MoneyModel.from(shippingMethod.tax);
    this._taxRate = shippingMethod.taxRate;

    // UNCOMMENT WHEN USING DATES INSTEAD OF DAYS IN DELIVERY RANGES

    // // Empty string is falsy,
    // // so checking if the following dates are nullish instead.
    // this.latestDeliveryDays = !isNullish(shippingMethod.latestDeliveryDays)
    //   ? timeStampableToDate(shippingMethod.latestDeliveryDays)
    //   : shippingMethod.latestDeliveryDays;

    // this.earliestDeliveryDays = !isNullish(shippingMethod.earliestDeliveryDays)
    //   ? timeStampableToDate(shippingMethod.earliestDeliveryDays)
    //   : shippingMethod.earliestDeliveryDays;

    // Empty string is falsy,
    // so checking if the following dates are nullish instead.
    this.latestDeliveryDays = shippingMethod.latestDeliveryDays;
    this.earliestDeliveryDays = shippingMethod.earliestDeliveryDays;
    this.earliestDeliveryDateOverride =
      shippingMethod.earliestDeliveryDateOverride;
    this.latestDeliveryDateOverride = shippingMethod.latestDeliveryDateOverride;
    this.formattedDeliveryEstimate = shippingMethod.formattedDeliveryEstimate;
    this.deliveryMessageHTML = shippingMethod.deliveryMessageHTML;
    this.additionalMessage = shippingMethod.additionalMessage;
  }

  /** @inheritdoc */
  public get taxRate(): number {
    return this._taxRate;
  }

  /** @inheritdoc */
  public override update(dto: Partial<DTO<IShippingMethod>>): void {
    if (dto.tax) {
      this.tax.update(dto.tax);
    }

    if (dto.taxRate !== undefined && Number.isFinite(dto.taxRate)) {
      this._taxRate = dto.taxRate;
    }
  }

  /** @inheritdoc */
  public toDTO(): DTO<IShippingMethod> {
    return {
      id: this.id,
      uid: this.uid,
      country: this.country,
      brand: this.brand,
      name: this.name,
      description: this.description,
      shippingCost: this.shippingCost.toDTO(),
      tax: this.tax.toDTO(),
      taxRate: this.taxRate,
      cutoffTime: this.cutoffTime,
      cutoffDisplay: this.cutoffDisplay,

      latestDeliveryDays: this.latestDeliveryDays,
      earliestDeliveryDays: this.earliestDeliveryDays,
      earliestDeliveryDateOverride: this.earliestDeliveryDateOverride,
      latestDeliveryDateOverride: this.latestDeliveryDateOverride,
      formattedDeliveryEstimate: this.formattedDeliveryEstimate,
      deliveryMessageHTML: this.deliveryMessageHTML,
      additionalMessage: this.additionalMessage
    };
  }
}
